import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { api, API, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Save, Image as ImageIcon, Check, Plus, X, ArrowLeft, ArrowRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const abs = (url) => (!url ? url : url.startsWith("http") ? url : url.startsWith("/api/") ? API.replace(/\/api$/, "") + url : url);

export default function AdminSite() {
  const { user } = useAuth();
  const { settings, refresh } = useSite();
  const [draft, setDraft] = useState(null);
  const [media, setMedia] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pickerFor, setPickerFor] = useState(null); // path to nested field

  useEffect(() => { if (user?.role === "admin") api.get("/media").then(r => setMedia(r.data)).catch(()=>{}); }, [user]);
  useEffect(() => { if (settings) setDraft(JSON.parse(JSON.stringify(settings))); }, [settings]);

  if (user === undefined) return <div className="pt-40 container-lux">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin/site" replace />;
  if (user.role !== "admin") return <div className="pt-40 container-lux"><h1 className="font-serif text-4xl">Admin access required</h1></div>;
  if (!draft) return <div className="pt-40 container-lux">Loading settings…</div>;

  // helper: set a nested path like "home.editorial.title_line1" or "logo.dark"
  const setPath = (path, value) => {
    const parts = path.split(".");
    const next = { ...draft };
    let cur = next;
    for (let i = 0; i < parts.length - 1; i++) {
      cur[parts[i]] = { ...(cur[parts[i]] || {}) };
      cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = value;
    setDraft(next);
  };
  const getPath = (path) => {
    if (!path) return undefined;
    return path.split(".").reduce((o, k) => (o == null ? o : o[k]), draft);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", draft);
      toast.success("Site settings saved");
      await refresh();
    } catch (e) { toast.error(formatErr(e)); }
    setSaving(false);
  };

  const addStat = () => setPath("home.editorial.stats", [...(getPath("home.editorial.stats") || []), { value: "", label: "" }]);
  const removeStat = (i) => setPath("home.editorial.stats", (getPath("home.editorial.stats") || []).filter((_, k) => k !== i));

  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen" data-testid="admin-site-page">
      <div className="container-lux">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <p className="overline text-muted-foreground">Atelier · Admin</p>
            <h1 className="mt-4 font-serif tracking-tighter text-5xl md:text-6xl font-light">Site Settings</h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl">Upload your logo and edit every editorial section on the homepage. Changes go live the moment you save.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/admin/nav" className="lux-btn-outline py-3 px-5 text-[10px]">Nav Editor</Link>
            <button onClick={save} disabled={saving} className="lux-btn-dark py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="save-site"><Save size={12} strokeWidth={1} /> {saving ? "Saving…" : "Save Changes"}</button>
          </div>
        </div>

        <Tabs defaultValue="logo" className="mt-14">
          <TabsList className="w-full justify-start bg-transparent border-b border-border p-0 h-auto rounded-none">
            {[["logo","Logo"],["home","Home Copy"],["auth","Login Image"],["sections","Section Toggles"]].map(([v,l]) => (
              <TabsTrigger key={v} value={v} className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-ink data-[state=active]:shadow-none data-[state=active]:bg-transparent px-0 mr-10 pb-4 text-xs uppercase tracking-widest">{l}</TabsTrigger>
            ))}
          </TabsList>

          {/* LOGO */}
          <TabsContent value="logo" className="pt-10">
            <div className="grid md:grid-cols-2 gap-10">
              <LogoField label="Dark Logo · shown on light backgrounds" path="logo.dark" get={getPath} set={setPath} pickerFor={pickerFor} setPickerFor={setPickerFor} preview="light" />
              <LogoField label="Light Logo · shown on the dark hero and footer" path="logo.light" get={getPath} set={setPath} pickerFor={pickerFor} setPickerFor={setPickerFor} preview="dark" />
            </div>
          </TabsContent>

          {/* HOME */}
          <TabsContent value="home" className="pt-10 space-y-16">
            <SectionBlock title="Collections Header">
              <TextField label="Kicker" value={getPath("home.collections_kicker")} onChange={v=>setPath("home.collections_kicker", v)} testid="s-collections-kicker" />
              <TextField label="Title" value={getPath("home.collections_title")} onChange={v=>setPath("home.collections_title", v)} testid="s-collections-title" />
              <TextArea label="Description" value={getPath("home.collections_desc")} onChange={v=>setPath("home.collections_desc", v)} testid="s-collections-desc" />
            </SectionBlock>

            <SectionBlock title="New Arrivals Header">
              <TextField label="Kicker" value={getPath("home.new_arrivals_kicker")} onChange={v=>setPath("home.new_arrivals_kicker", v)} testid="s-na-kicker" />
              <TextField label="Title" value={getPath("home.new_arrivals_title")} onChange={v=>setPath("home.new_arrivals_title", v)} testid="s-na-title" />
            </SectionBlock>

            <SectionBlock title="Editorial Section">
              <div className="grid md:grid-cols-2 gap-6">
                <TextField label="Kicker" value={getPath("home.editorial.kicker")} onChange={v=>setPath("home.editorial.kicker", v)} testid="s-ed-kicker" />
                <TextField label="Title line 1" value={getPath("home.editorial.title_line1")} onChange={v=>setPath("home.editorial.title_line1", v)} testid="s-ed-t1" />
                <TextField label="Title line 2 (italic)" value={getPath("home.editorial.title_line2")} onChange={v=>setPath("home.editorial.title_line2", v)} testid="s-ed-t2" />
                <TextField label="Button 1 Text" value={getPath("home.editorial.cta_text")} onChange={v=>setPath("home.editorial.cta_text", v)} testid="s-ed-cta" />
                <TextField label="Button 1 Link" value={getPath("home.editorial.cta_link")} onChange={v=>setPath("home.editorial.cta_link", v)} testid="s-ed-link" />
                <TextField label="Button 2 Text" value={getPath("home.editorial.cta2_text")} onChange={v=>setPath("home.editorial.cta2_text", v)} testid="s-ed-cta2" />
                <TextField label="Button 2 Link" value={getPath("home.editorial.cta2_link")} onChange={v=>setPath("home.editorial.cta2_link", v)} testid="s-ed-link2" />
              </div>
              <TextArea label="Description" value={getPath("home.editorial.description")} onChange={v=>setPath("home.editorial.description", v)} rows={4} testid="s-ed-desc" />
              <ImageField label="Image" path="home.editorial.image" get={getPath} set={setPath} pickerFor={pickerFor} setPickerFor={setPickerFor} />
              <div>
                <div className="flex items-baseline justify-between">
                  <label className="overline">Stats</label>
                  <button onClick={addStat} className="lux-link inline-flex items-center gap-1" data-testid="add-stat"><Plus size={12} strokeWidth={1} /> Add stat</button>
                </div>
                <ul className="mt-4 space-y-3">
                  {(getPath("home.editorial.stats") || []).map((s, i) => (
                    <li key={i} className="grid grid-cols-[1fr_2fr_40px] gap-3">
                      <input value={s.value} onChange={(e)=>{ const list = [...getPath("home.editorial.stats")]; list[i] = { ...list[i], value: e.target.value }; setPath("home.editorial.stats", list); }} placeholder="350+" className="lux-input" data-testid={`stat-v-${i}`} />
                      <input value={s.label} onChange={(e)=>{ const list = [...getPath("home.editorial.stats")]; list[i] = { ...list[i], label: e.target.value }; setPath("home.editorial.stats", list); }} placeholder="Hours per piece" className="lux-input" data-testid={`stat-l-${i}`} />
                      <button onClick={()=>removeStat(i)} className="p-2 text-destructive" data-testid={`stat-x-${i}`}><X size={13} strokeWidth={1} /></button>
                    </li>
                  ))}
                </ul>
              </div>
            </SectionBlock>

            <SectionBlock title="Bridal Appointment">
              <div className="grid md:grid-cols-2 gap-6">
                <TextField label="Kicker" value={getPath("home.bridal.kicker")} onChange={v=>setPath("home.bridal.kicker", v)} testid="s-br-kicker" />
                <TextField label="Title line 1" value={getPath("home.bridal.title_line1")} onChange={v=>setPath("home.bridal.title_line1", v)} testid="s-br-t1" />
                <TextField label="Title line 2 (italic)" value={getPath("home.bridal.title_line2")} onChange={v=>setPath("home.bridal.title_line2", v)} testid="s-br-t2" />
              </div>
              <TextArea label="Description" value={getPath("home.bridal.description")} onChange={v=>setPath("home.bridal.description", v)} rows={3} testid="s-br-desc" />
              <ImageField label="Image" path="home.bridal.image" get={getPath} set={setPath} pickerFor={pickerFor} setPickerFor={setPickerFor} />
            </SectionBlock>
          </TabsContent>

          {/* SECTIONS */}
          {/* SECTIONS */}
          <TabsContent value="auth" className="pt-10 space-y-8">
            <p className="text-sm text-muted-foreground max-w-xl">The image and tagline shown on the left side of the sign-in, sign-up and forgot-password pages.</p>
            <div className="grid md:grid-cols-2 gap-10">
              <ImageField label="Login Image" path="auth.image" get={getPath} set={setPath} pickerFor={pickerFor} setPickerFor={setPickerFor} />
              <div className="space-y-6">
                <TextArea label="Tagline" value={getPath("auth.tagline")} onChange={v=>setPath("auth.tagline", v)} rows={3} testid="s-auth-tag" />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="sections" className="pt-10">
            <p className="text-sm text-muted-foreground max-w-xl">Toggle any homepage section on or off. Hero and Collections are always visible.</p>
            <ul className="mt-8 space-y-3 max-w-lg">
              {[["editorial","Editorial Section"],["new_arrivals","New Arrivals"],["bridal","Bridal Appointment"],["testimonials","Testimonials"],["instagram","Instagram Feed"]].map(([k, l]) => (
                <li key={k} className="flex items-center justify-between border border-border px-6 py-4">
                  <span>{l}</span>
                  <label className="inline-flex items-center gap-3 text-xs uppercase tracking-widest">
                    <input type="checkbox" checked={getPath(`home.sections.${k}`) !== false} onChange={(e)=>setPath(`home.sections.${k}`, e.target.checked)} data-testid={`sec-${k}`} />
                    {getPath(`home.sections.${k}`) !== false ? "Live" : "Hidden"}
                  </label>
                </li>
              ))}
            </ul>
          </TabsContent>
        </Tabs>
      </div>

      {/* Media picker */}
      <Dialog open={!!pickerFor} onOpenChange={(v)=>!v && setPickerFor(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-ivory border border-border rounded-none">
          <DialogHeader><DialogTitle className="font-serif text-3xl font-light tracking-tighter">Media Library</DialogTitle></DialogHeader>
          {media.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-6">Library empty. <Link to="/admin/media" className="underline">Upload files</Link> first.</p>
          ) : (
            <ul className="mt-6 grid grid-cols-3 md:grid-cols-5 gap-4">
              {media.filter(m => m.content_type?.startsWith("image/")).map(m => {
                const url = `/api/media/${m.id}`;
                const active = getPath(pickerFor) === url || getPath(pickerFor) === `${API}/media/${m.id}`;
                return (
                  <button key={m.id} type="button" onClick={()=>{ setPath(pickerFor, url); setPickerFor(null); }}
                    data-testid={`site-pick-${m.id}`}
                    className={`relative aspect-square overflow-hidden border transition-colors ${active ? "border-ink ring-2 ring-ink" : "border-border hover:border-ink"}`}>
                    <img src={`${API}/media/${m.id}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                    {active && <span className="absolute top-2 right-2 w-6 h-6 bg-ink text-ivory flex items-center justify-center"><Check size={12} strokeWidth={1.5} /></span>}
                  </button>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LogoField({ label, path, get, set, pickerFor, setPickerFor, preview }) {
  const url = get(path);
  const bgClass = preview === "dark" ? "bg-ink" : "bg-ivory border border-border";
  return (
    <div>
      <p className="overline">{label}</p>
      <div className={`mt-4 h-40 flex items-center justify-center ${bgClass}`}>
        {url ? <img src={abs(url)} alt="" className="max-h-16 object-contain" /> : <ImageIcon strokeWidth={0.6} size={32} className={preview === "dark" ? "text-ivory/40" : "text-muted-foreground"} />}
      </div>
      <label className="overline text-muted-foreground mt-6 block">Image URL</label>
      <input value={url || ""} onChange={(e)=>set(path, e.target.value)} className="lux-input" data-testid={`logo-url-${path.split(".").pop()}`} placeholder="https://… or /api/media/{id}" />
      <button type="button" onClick={()=>setPickerFor(path)} className="lux-link mt-3 inline-block" data-testid={`logo-pick-${path.split(".").pop()}`}>Pick from library →</button>
    </div>
  );
}
function ImageField({ label, path, get, set, pickerFor, setPickerFor }) {
  const url = get(path);
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="overline">{label}</label>
        <button type="button" onClick={()=>setPickerFor(path)} className="lux-link" data-testid={`img-pick-${path}`}>From library →</button>
      </div>
      {url ? (
        <div className="mt-4 aspect-[4/3] bg-muted overflow-hidden border border-border max-w-sm">
          <img src={abs(url)} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="mt-4 aspect-[4/3] border border-dashed border-border max-w-sm flex items-center justify-center text-xs text-muted-foreground">Pick an image</div>
      )}
      <input value={url || ""} onChange={(e)=>set(path, e.target.value)} className="lux-input mt-4" placeholder="https://… or /api/media/{id}" />
    </div>
  );
}
function TextField({ label, value, onChange, testid, ...rest }) {
  return (
    <div>
      <label className="overline text-muted-foreground">{label}</label>
      <input value={value || ""} onChange={(e)=>onChange(e.target.value)} data-testid={testid} className="lux-input" {...rest} />
    </div>
  );
}
function TextArea({ label, value, onChange, rows = 3, testid, ...rest }) {
  return (
    <div>
      <label className="overline text-muted-foreground">{label}</label>
      <textarea value={value || ""} onChange={(e)=>onChange(e.target.value)} rows={rows} data-testid={testid} className="w-full bg-transparent border border-border p-3 text-sm focus:outline-none focus:border-ink" {...rest} />
    </div>
  );
}
function SectionBlock({ title, children }) {
  return (
    <div>
      <h2 className="font-serif text-2xl tracking-tighter font-light border-b border-border pb-3 mb-6">{title}</h2>
      <div className="space-y-6">{children}</div>
    </div>
  );
}
